import { cn, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { LandTypePctDistribution } from '@/api/rest/resources/types/organization';
import { PlotType } from '@/api/rest/resources/types/plot';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartShape, PieChartConfig } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { useDashboardOrganizationSummary } from '@/hooks/useDashboardOrganizationSummary';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useLandTypeDistribution } from '@/hooks/useOrganizationOverview';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';
import { squareMetersToHectares } from '@/utils/plot';

type LandUseDistributionChartProps = {
  styles?: Record<string, string>;
};

export const useLandUseDistributionChartConfiguration = (props?: LandUseDistributionChartProps) => {
  const { data } = useLandTypeDistribution();
  const { t } = useTranslation();
  const { total_size: totalSize, total_assets: totalAssets } = useDashboardOrganizationSummary().data ?? {};

  const noChartData = isNullish(data) || data.length === 0;

  const chartConfig: PieChartConfig[] = [
    {
      shape: ChartShape.pie,
      id: 'land_use_distribution',
      key: 'land_use_distribution',
      dataKey: 'value',
      innerRadius: 90,
      outerRadius: 108,
      startAngle: 90,
      endAngle: 450,
      paddingAngle: 0,
      cx: '50%',
      cy: '50%',
    },
  ];

  const dataWithColors = data.map((item) => ({
    ...item,
    color: getColorForLandUse(item.name),
  }));

  const legendItems = data.map((item) => ({
    icon: 'ri-circle-fill',
    className: 'bg-neutral-black-4 p-2 rounded-lg',
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    label: `${t(`global.plotTypes.${item.name}`)} ${item.value}%`,
    color: getColorForLandUse(item.name),
  }));

  const formattedTotalSize = getDisplayNumber(squareMetersToHectares(totalSize ?? 0), window.navigator.language);
  const formattedTotalAssets = totalAssets ?? 0;

  const chartWidget = {
    name: 'land_use_distribution_graph',
    type: WidgetType.o_composite_graph,
    visible: true,
    relatedData: {
      'data-testid': 'land-use-distribution-chart',
      data: dataWithColors,
      chartConfig,
      legendItems,
      tooltip: CustomTooltip,
    },
  };

  const statsWidget = {
    name: 'land_use_distribution_stats',
    type: WidgetType.a_stack,
    visible: true,
    relatedData: {
      className: 'flex flex-col w-full justify-between px-0 py-0 gap-4 w-[300px]',
    },
    widgets: [
      {
        name: 'land_use_distribution_stats',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'flex flex-col justify-center px-0 gap-4 w-fit',
        },
        widgets: [
          {
            name: 'land_use_total_size_label',
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              i18nPath: 'global.widget.totalSize',
              className: 'typography-body1 text-text-secondary',
            },
          },
          {
            name: 'land_use_total_size_value',
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              text: `${formattedTotalSize} ha`,
              className: 'typography-h2 text-text-primary',
            },
          },
        ],
      },
      {
        name: 'land_use_distribution_stats',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'flex flex-col justify-center px-0 gap-4 w-fit',
        },
        widgets: [
          {
            name: 'land_use_total_assets_label',
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              i18nPath: 'global.widget.totalAssets',
              className: 'typography-body1 text-text-secondary mt-4',
            },
          },
          {
            name: 'land_use_total_assets_value',
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              text: `${formattedTotalAssets}`,
              className: 'typography-h2 text-text-primary',
            },
          },
        ],
      },
    ],
  };

  const widgets = [
    {
      name: 'land_use_distribution_content',
      type: WidgetType.a_stack,
      visible: true,
      relatedData: {
        className: 'flex flex-row items-center justify-center w-full gap-4',
      },
      widgets: [chartWidget, statsWidget],
    },
  ];

  return {
    name: 'land_use_distribution_card',
    type: WidgetType.o_card,
    visible: !noChartData,
    relatedData: {
      styles: {
        root: cn('h-full rounded-2xl w-full', props?.styles?.root ?? ''),
        header: cn(
          'typography-overline p-4 justify-center items-center text-text-secondary border-none',
          props?.styles?.header ?? '',
        ),
        body: cn('h-full flex flex-col justify-center p-2 border-none', props?.styles?.body ?? ''),
      },
      header: t('global.widget.landDistribution'),
    },
    widgets,
  };
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload: LandTypePctDistribution;
  }>;
};

const getColorForLandUse = (name: string) => {
  return colorPalette[`landtypes-${name}-main`] ?? colorPalette['neutral-black-12'];
};

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className='rounded-md bg-white-100 px-3 py-2 shadow-lg border border-neutral-black-12'>
      <Stack direction='row' centerMain spacing={2} className='mb-1'>
        <div className='w-3 h-3 rounded-full' style={{ backgroundColor: getColorForLandUse(data.name) }} />
        <span className='font-medium text-sm text-gray-900'>{t(`global.plotTypes.${data.name as PlotType}`)}</span>
      </Stack>
      <div className='text-sm text-gray-600'>{getDisplayNumber(data.value, window.navigator.language)}%</div>
    </div>
  );
};
